import { useEffect, useRef, useState } from 'react';

// Polls /version.json (a build-stamped Unix timestamp) and flips true the
// first time the live file differs from the value seen on mount — i.e. a new
// deploy has landed while this tab was open. Plain string inequality, any
// change counts; no "is newer" logic. Network errors are swallowed so a blip
// never falsely triggers a reload prompt.
//
// Also checks on tab visibility-change and window focus so a tab idle
// overnight surfaces the banner the moment the user comes back, instead of
// waiting up to the full poll interval.

const POLL_MS = 2 * 60 * 1000;          // 2 minutes — cheap, more responsive
const MIN_GAP_MS = 10 * 1000;           // collapse rapid focus/visibility events

// Log every poll so deploys-not-firing-banner cases can be diagnosed from
// the browser console without code changes. Cheap (one line per 2 minutes).
const log = (...args) => { try { console.info('[version-check]', ...args); } catch {} };

const fetchVersion = async () => {
  // Belt-and-braces cache-busting: random query (some CDNs ignore short keys
  // or strip them), no-store cache mode, and an explicit Cache-Control
  // request header so any HTTP/1.1 intermediate sees the no-cache intent.
  const bust = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const res = await fetch(`/version.json?_=${bust}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' },
  });
  if (!res.ok) throw new Error(`version.json ${res.status}`);
  const { version } = await res.json();
  return version;
};

const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const currentRef = useRef(null);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const check = async ({ force = false, reason = 'tick' } = {}) => {
      if (!force && Date.now() - lastCheckRef.current < MIN_GAP_MS) return;
      lastCheckRef.current = Date.now();
      try {
        const version = await fetchVersion();
        if (cancelled || version == null) {
          log(`fetch returned no version (cancelled=${cancelled})`);
          return;
        }
        if (currentRef.current == null) {
          currentRef.current = version;
          log(`seeded · current = ${version} · reason=${reason}`);
        } else if (version !== currentRef.current) {
          log(`NEW version · ${currentRef.current} → ${version} · reason=${reason}`);
          setUpdateAvailable(true);
        } else {
          log(`no change · ${version} · reason=${reason}`);
        }
      } catch (err) {
        log(`fetch failed: ${err?.message || err}`);
      }
    };

    check({ force: true, reason: 'mount' });
    const id = setInterval(() => check({ reason: 'interval' }), POLL_MS);

    // Check immediately when the tab regains attention. Most deploys happen
    // when the user is away from the screen, so this catches them on return.
    const onVisible = () => { if (document.visibilityState === 'visible') check({ reason: 'visible' }); };
    const onFocus   = () => check({ reason: 'focus' });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    // Manual trigger from DevTools — `__checkVersion()` forces a fetch and
    // logs the result so deploys-not-firing can be diagnosed live.
    window.__checkVersion = () => check({ force: true, reason: 'manual' });

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      try { delete window.__checkVersion; } catch {}
    };
  }, []);

  return updateAvailable;
};

export default useVersionCheck;
